'use client'

import {
  BreadcrumbList,
  BreadcrumbListCollapsedItems,
  BreadcrumbListItem,
} from '@cleanlab/design-system/components'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { validate as isValidUUID } from 'uuid'

import { useGetProject } from '@/api/projects'
import { type ProjectDetailsSchema } from '@/client/types.gen'
import Shimmer from '@/components/Shimmer/Shimmer'
import { APP_PATHS } from '@/global/consts'

type BreadcrumbItem = {
  name: string | React.ReactElement
  href: string
}

const defaultSegmentToCrumb = ({
  segment,
  i,
  pathSegments,
  urlPrefix = '',
}: {
  segment: string
  i: number
  pathSegments: string[]
  urlPrefix?: string
}) => ({
  name: isValidUUID(segment)
    ? segment
    : segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
  href: `${urlPrefix}/${pathSegments.slice(0, i + 1).join('/')}`,
})

const defaultGetBreadcrumbs = (pathSegments: string[], opts?: { urlPrefix: string }) => {
  return pathSegments.map((segment, i) =>
    defaultSegmentToCrumb({ segment, i, pathSegments, urlPrefix: opts?.urlPrefix })
  )
}

const PATH_HELPERS = {
  projects: {
    getBreadcrumbs: (
      pathSegments: string[],
      opts?: { project: ProjectDetailsSchema | undefined; isLoadingProject: boolean }
    ) => {
      const basePath = pathSegments[0]
      const projectId = pathSegments[1]

      const projectName = opts?.isLoadingProject ? (
        <Shimmer height={16} width={100} />
      ) : (
        projectId &&
        (opts?.project?.name ||
          defaultSegmentToCrumb({ segment: projectId, i: 1, pathSegments }).name)
      )

      return [
        {
          name: 'Projects',
          href: `/${basePath}`,
        },
        ...(projectId
          ? [
              {
                name: projectName,
                href: `/${basePath}/${projectId}`,
              },
            ]
          : []),
        ...defaultGetBreadcrumbs(pathSegments.slice(2), {
          urlPrefix: `/${pathSegments.slice(0, 1).join('/')}`,
        }),
      ]
    },
  },
  default: {
    getBreadcrumbs: defaultGetBreadcrumbs,
  },
} as const satisfies Record<
  string,
  {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getBreadcrumbs: (pathSegments: string[], opts?: any) => BreadcrumbItem[]
  }
>

const VALID_TOP_LEVEL_ROUTES: string[] = [
  APP_PATHS.PROJECTS,
  APP_PATHS.ACCOUNT,
  APP_PATHS.ADMIN,
] as const

export function BreadcrumbNav() {
  const pathname = usePathname()
  const router = useRouter()
  const pathSegments = pathname.split('/').filter((segment) => segment)
  const topSection = `/${pathSegments[0]}`

  const { data: project, isLoading: isLoadingProject } = useGetProject(pathSegments[1], {
    enabled: topSection === APP_PATHS.PROJECTS && !!pathSegments[1],
  })

  // If we have path segments but the top section is not valid, this is a 404
  // Only show "Codex" in breadcrumb for 404 pages
  const isValidRoute = !topSection || VALID_TOP_LEVEL_ROUTES.includes(topSection)

  const breadcrumbData = (() => {
    if (!isValidRoute) {
      return []
    }

    if (topSection === APP_PATHS.PROJECTS) {
      return PATH_HELPERS.projects.getBreadcrumbs(pathSegments, { project, isLoadingProject })
    } else {
      return PATH_HELPERS.default.getBreadcrumbs(pathSegments)
    }
  })()

  return (
    <div className="w-full overflow-hidden">
      <BreadcrumbList>
        <BreadcrumbListItem asChild collapsible={false} onDropdownSelect={() => router.push('/')}>
          <Link href="/">Codex</Link>
        </BreadcrumbListItem>
        <BreadcrumbListCollapsedItems />
        {breadcrumbData.map(({ href, name }, index) => {
          const isLast = index === pathSegments.length - 1
          const key = `${href}-${index}`

          if (isLast) {
            return <BreadcrumbListItem key={key}>{name}</BreadcrumbListItem>
          }
          return (
            <BreadcrumbListItem key={key} onDropdownSelect={() => router.push(href)} asChild>
              <Link href={href}>{name}</Link>
            </BreadcrumbListItem>
          )
        })}
      </BreadcrumbList>
    </div>
  )
}
